import { computed, ref } from 'vue'
import * as pdfjs from 'pdfjs-dist'
import { initPdfWorker } from '@/composables/pdf/initPdfWorker'

const isPdfFile = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

const isAbortError = (error: unknown) =>
  typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'

const createAbortError = () => {
  const error = new Error('Aborted')
  error.name = 'AbortError'
  return error
}

export const usePasswordProtectedPdfCheck = () => {
  const activeChecks = ref(0)
  const isChecking = computed(() => activeChecks.value > 0)

  const check = async (file: File, signal?: AbortSignal): Promise<boolean> => {
    if (!isPdfFile(file)) return false

    if (signal?.aborted) throw createAbortError()

    const data = new Uint8Array(await file.arrayBuffer())

    if (signal?.aborted) throw createAbortError()

    initPdfWorker()
    activeChecks.value += 1

    return new Promise<boolean>((resolve, reject) => {
      let settled = false

      const finish = (fn: () => void) => {
        if (settled) return
        settled = true
        activeChecks.value -= 1
        signal?.removeEventListener('abort', handleAbort)
        fn()
      }

      const handleAbort = () => {
        finish(() => {
          reject(createAbortError())
        })
      }

      signal?.addEventListener('abort', handleAbort, { once: true })

      const loadingTask = pdfjs.getDocument({ data })

      loadingTask.onPassword = () => {
        void loadingTask.destroy()
        finish(() => {
          resolve(true)
        })
      }

      loadingTask.promise.then(
        (doc) => {
          void doc.cleanup()
          finish(() => {
            resolve(false)
          })
        },
        (error: unknown) => {
          finish(() => {
            if (isAbortError(error)) {
              reject(error instanceof Error ? error : new Error(String(error)))
            } else {
              resolve(false)
            }
          })
        },
      )
    })
  }

  return { isChecking, check }
}
